// ═══════════════════════════════════════════════════════════════════════════
// useDialogoModal — el teclado de un diálogo modal (report XmVfaj3YByq6M6c9XPmo)
// ═══════════════════════════════════════════════════════════════════════════
// Los diálogos del core declaran `aria-modal="true"` (report 0BQntEBh39kAOyeWa0Ga):
// un lector de pantalla trata el fondo como inerte. Sin esto el Tab igual salía
// del diálogo hacia ese fondo, y quien navega con teclado quedaba operando la
// página de atrás sin saberlo. Lo que hace, una sola vez para los cuatro
// (`Modal`, `ConfirmModal`, `SudoModal`, `CrearAccionableModal`):
//
//   · al abrir, el foco entra al diálogo (al contenedor, salvo que un campo ya
//     lo haya tomado con `autoFocus` o su propio efecto);
//   · Tab y Shift+Tab dan la vuelta adentro;
//   · Escape llama a `onCerrar`, salvo `bloqueado` (una operación en curso), un
//     Escape que otro control ya consumió (`preventDefault`, como `Combobox`) o
//     uno tecleado en un campo editable: los editores inline del árbol usan
//     Escape para cancelar ESE campo, y cerrar el diálogo encima perdería lo
//     que la persona escribió en el resto;
//   · al cerrar, el foco vuelve a donde estaba.
//
// Con diálogos apilados (un `SudoModal` sobre un `Modal`) sólo el de arriba
// atiende el teclado: por eso la pila, que se arma por orden de apertura.
//
// Se escucha con el `onKeyDown` de React en el diálogo y no en `document`: así
// el handler de un control de adentro corre ANTES y puede consumir el Escape, y
// un popover en portal (MenuAlViewport) no cierra el diálogo, porque su nodo no
// está dentro del del diálogo.
import { useCallback, useEffect, useRef } from 'react'

const ENFOCABLES = [
  'a[href]', 'area[href]', 'button:not([disabled])', 'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])', 'textarea:not([disabled])', 'iframe', '[contenteditable="true"]',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

const pila = []

const esCampoEditable = (el) =>
  !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable)

/**
 * @param {{ abierto: boolean, onCerrar?: () => void, bloqueado?: boolean }} opciones
 * @returns {{ ref: import('react').RefObject<HTMLElement>, tabIndex: -1, onKeyDown: (e: KeyboardEvent) => void }}
 *   props para el elemento con `role="dialog"`.
 */
export function useDialogoModal({ abierto, onCerrar, bloqueado = false }) {
  const ref = useRef(null)
  const cerrarRef = useRef(onCerrar)
  cerrarRef.current = onCerrar
  const bloqueadoRef = useRef(bloqueado)
  bloqueadoRef.current = bloqueado
  const tokenRef = useRef(null)

  useEffect(() => {
    if (!abierto) return undefined
    const token = {}
    tokenRef.current = token
    pila.push(token)
    const previo = document.activeElement
    const nodo = ref.current
    if (nodo && !nodo.contains(document.activeElement)) nodo.focus()
    return () => {
      const i = pila.indexOf(token)
      if (i >= 0) pila.splice(i, 1)
      if (previo && typeof previo.focus === 'function' && document.contains(previo)) previo.focus()
    }
  }, [abierto])

  const onKeyDown = useCallback((e) => {
    const nodo = ref.current
    if (!nodo || pila[pila.length - 1] !== tokenRef.current || !nodo.contains(e.target)) return
    if (e.key === 'Escape') {
      if (bloqueadoRef.current || e.defaultPrevented || esCampoEditable(e.target)) return
      e.preventDefault()
      cerrarRef.current?.()
      return
    }
    if (e.key !== 'Tab') return
    const enfocables = [...nodo.querySelectorAll(ENFOCABLES)]
    if (enfocables.length === 0) { e.preventDefault(); nodo.focus(); return }
    const primero = enfocables[0]
    const ultimo = enfocables[enfocables.length - 1]
    if (e.shiftKey && (document.activeElement === primero || document.activeElement === nodo)) {
      e.preventDefault()
      ultimo.focus()
    } else if (!e.shiftKey && document.activeElement === ultimo) {
      e.preventDefault()
      primero.focus()
    }
  }, [])

  return { ref, tabIndex: -1, onKeyDown }
}
